import { useState } from 'react'
import { Button, DateInput, Modal } from '@/components/ui'
import { ExitTypeSelect } from '@/components/common/ExitTypeSelect'
import type { ExitType } from '@/lib/exitTypes'
import type { UserListItem } from '@/types'
import { useTerminateUser } from '../hooks/useUsers'

export interface TerminateUserModalProps {
  user: UserListItem | null
  onClose: () => void
}

export function TerminateUserModal({ user, onClose }: TerminateUserModalProps): React.ReactElement {
  const terminateUser = useTerminateUser()
  const [exitDate, setExitDate] = useState(new Date().toISOString().slice(0, 10))
  const [exitReason, setExitReason] = useState('')
  const [exitDateError, setExitDateError] = useState<string | undefined>()
  // A terminate is a termination unless the admin says otherwise.
  const [exitType, setExitType] = useState<ExitType>('terminated')

  // Reset the form whenever a different employee is opened (state adjusted
  // during render, so the first paint already shows the fresh values).
  const [formUser, setFormUser] = useState(user)
  if (user !== formUser) {
    setFormUser(user)
    if (user) {
      setExitDate(new Date().toISOString().slice(0, 10))
      setExitReason('')
      setExitType('terminated')
      setExitDateError(undefined)
    }
  }

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    if (!user) return
    if (!exitDate) {
      setExitDateError('Exit date is required')
      return
    }
    terminateUser.mutate(
      { id: user._id, payload: { exitDate, exitReason: exitReason || undefined, exitType } },
      { onSuccess: onClose }
    )
  }

  return (
    <Modal
      open={!!user}
      onClose={onClose}
      title="Terminate employee"
      className="max-w-md"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            form="terminate-user-form"
            type="submit"
            variant="danger"
            loading={terminateUser.isPending}
          >
            {terminateUser.isPending ? 'Terminating...' : 'Terminate'}
          </Button>
        </>
      }
    >
      <form id="terminate-user-form" onSubmit={handleSubmit} noValidate className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Terminating{' '}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {user?.name ?? 'this employee'}
          </span>{' '}
          will mark them exited, sign them out, deactivate their company memberships and end their
          People &amp; HR employment on the exit date.
        </p>
        <div>
          <DateInput
            label="Exit date"
            required
            value={exitDate}
            error={exitDateError}
            onChange={(event) => {
              setExitDate(event.target.value)
              if (event.target.value) setExitDateError(undefined)
            }}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            The last day they work. Shifts after this date are refused.
          </p>
        </div>
        <ExitTypeSelect id="terminate-exit-type" value={exitType} onChange={setExitType} />
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Exit reason
          </span>
          <textarea
            value={exitReason}
            onChange={(event) => setExitReason(event.target.value)}
            rows={4}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </label>
      </form>
    </Modal>
  )
}

TerminateUserModal.displayName = 'TerminateUserModal'
